import type { Branch } from './branch.types';

export interface BranchAppearanceSources {
  chosenBranchId?: string;
  accessibleBranches: Branch[];
  branches: Branch[];
  savedBranchId: string | null;
}

export interface BranchAppearanceSelection {
  branch: Branch | null;
  /** True when the branch was chosen or fell back, so the caller that owns the saved id must store it. */
  shouldPersist: boolean;
}

const findById = (source: Branch[], id: string): Branch | null => {
  return source.find((branch) => branch.id === Number(id)) ?? null;
};

const lowestId = (source: Branch[]): Branch | null => {
  if (source.length === 0) return null;
  return source.reduce((minBranch, branch) => (branch.id < minBranch.id ? branch : minBranch), source[0]);
};

export function selectBranchForAppearance({
  chosenBranchId,
  accessibleBranches,
  branches,
  savedBranchId,
}: BranchAppearanceSources): BranchAppearanceSelection {
  const usingAccessibleBranches = accessibleBranches.length > 0;
  const source = usingAccessibleBranches ? accessibleBranches : branches;

  if (!Array.isArray(source) || source.length === 0) {
    return { branch: null, shouldPersist: false };
  }

  if (chosenBranchId !== undefined) {
    const chosenBranch = findById(source, chosenBranchId);
    if (chosenBranch) {
      return { branch: chosenBranch, shouldPersist: true };
    }
  }

  // A superuser's saved id is never replaced: when it is not among the accessible branches the
  // caller gets null, so no branch the user did not pick gets applied or stored (AC13). A USER's
  // stale saved id keeps falling back exactly as before (AC14).
  if (savedBranchId) {
    const savedBranch = findById(source, savedBranchId);
    if (usingAccessibleBranches || savedBranch) {
      return { branch: savedBranch, shouldPersist: false };
    }
  }

  const mainBranch = usingAccessibleBranches
    ? lowestId(source.filter((branch) => branch.isMain))
    : (source.find((branch) => branch.name === 'Matriz') ?? null);

  return { branch: mainBranch ?? lowestId(source), shouldPersist: true };
}
